import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { EMPTY } from 'rxjs';
import { mergeMap, switchMap } from 'rxjs/operators';

import { CustomerActivityService } from '@customer-activity/customer-activity.service';
import { ListingActivityConstants } from '@listings/constants/listing-activity-constants';
import { SetListingActivityRequest } from '@listings/models/api/set-listing-activity-request';
import { ListingHelperService } from '@listings/services/listing-helper.service';
import { ListingApiService } from '@listings/store/services/listing-api.service';
import { OnMarketStoreService } from '@on-market/store/services/on-market-store.service';
import * as listingActivityActions from '../actions/listing-activity.actions';
import { ListingsStoreService } from '../services/listings-store.service';

@Injectable()
export class ListingActivityEffects {

    constructor(
        private readonly actions$: Actions,
        private readonly listingApiService: ListingApiService,
        private readonly customerActivityService: CustomerActivityService,
        private readonly listingsStoreService: ListingsStoreService,
        private readonly onMarketStoreService: OnMarketStoreService,
    ) { }

    public readonly setListingsActivity$ = createEffect(() => this.actions$.pipe(
        ofType(listingActivityActions.setListingsActivity),
        concatLatestFrom(() => [this.listingsStoreService.getListings(), this.listingsStoreService.getCustomerListings()]),
        mergeMap(([{ request, customerId }, listings, customerListings]) => {
            const listingsWithoutRemoved = request.listingCandidates
                .filter(x =>
                    listings[x.id] != null && !listings[x.id].isDeleted ||
                    !customerListings.find(l => l.hashCode === x.hashCode).isDeleted
                );

            if (request.activity.id === ListingActivityConstants.Shown.id && listingsWithoutRemoved.some(listing => listing.isNewMatch)) {
                const pickedActivityRequest: SetListingActivityRequest = {
                    activity: ListingActivityConstants.PickListed,
                    listingCandidates: listingsWithoutRemoved.filter(listing => listing.isNewMatch),
                    isReactionRemoved: false,
                    notifyRequired: false
                };
                return this.listingApiService.setListingsActivity(pickedActivityRequest, customerId)
                    .pipe(mergeMap(() => this.listingApiService.setListingsActivity({ ...request, listingCandidates: listingsWithoutRemoved }, customerId)));
            }

            return this.listingApiService.setListingsActivity({ ...request, listingCandidates: listingsWithoutRemoved }, customerId);
        })
    ));

    public readonly setListingsActivitySuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(listingActivityActions.setListingsActivitySuccess),
                concatLatestFrom(() => [this.listingsStoreService.getListings(), this.onMarketStoreService.getActiveSavedSearchIdIfIsNotDefault()]),
                switchMap(([{ request }, listings, activeSavedSearchId]) => {
                    const activity = ListingHelperService.mapActivityToListingActivityType(request.activity.id);

                    if (activity == null || request.isReactionRemoved) {
                        return EMPTY;
                    }

                    const listingCategory = request.listingCandidates.reduce(
                        (result, { id }) => listings[id] == null ? result : [...result, { listingId: id, category: listings[id].category }],
                        []
                    );

                    return this.customerActivityService.addListingSessionActivity(activity, listingCategory, activeSavedSearchId);
                })
            ),
        { dispatch: false }
    );

    public readonly addListingSessionActivity$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(listingActivityActions.addListingSessionActivity),
                concatLatestFrom(() => this.onMarketStoreService.getActiveSavedSearchIdIfIsNotDefault()),
                switchMap(([{ listingActivityType, listingCategory }, activeSavedSearchId]) => {
                    return this.customerActivityService.addListingSessionActivity(listingActivityType, listingCategory, activeSavedSearchId);
                })
            ),
        { dispatch: false });
}